import { QUILL_MARK } from './quillMark';

export type SealMetal = 'gold' | 'silver' | 'bronze';
export type SealAccent = 'writer' | 'author' | 'bestseller';

interface MetalPalette {
  /** Radial gradient stops: highlight → face → edge. */
  g0: string;
  g1: string;
  g2: string;
  /** Thin line around the outer shape. */
  rim: string;
  /** Inner ring lines framing the text band. */
  ring: string;
  /** Engraved ring text. */
  ink: string;
  /** Default (no brand accent) emboss tones for the quill's original colours. */
  emboss: Record<string, string>;
}

const PALETTES: Record<SealMetal, MetalPalette> = {
  gold: {
    g0: '#ffdf8e',
    g1: '#f5b21d',
    g2: '#d68a00',
    rim: '#b87c00',
    ring: '#8a5f00',
    ink: '#6b4a03',
    emboss: {
      currentColor: '#523c02',
      '#F2385A': '#7a5504',
      '#F5A503': '#93670a',
      '#4AD9D9': '#6b4a03',
      '#36B1BF': '#5c4302',
    },
  },
  silver: {
    g0: '#f7f9fb',
    g1: '#c6ccd6',
    g2: '#98a0af',
    rim: '#79818f',
    ring: '#5d6472',
    ink: '#3e4450',
    emboss: {
      currentColor: '#333a45',
      '#F2385A': '#4a5262',
      '#F5A503': '#5d6575',
      '#4AD9D9': '#454c5a',
      '#36B1BF': '#3a414e',
    },
  },
  bronze: {
    g0: '#f0c396',
    g1: '#cd8f56',
    g2: '#a2622c',
    rim: '#7d4a1e',
    ring: '#61390f',
    ink: '#4a2c0a',
    emboss: {
      currentColor: '#3d2506',
      '#F2385A': '#5f3a0e',
      '#F5A503': '#754a15',
      '#4AD9D9': '#54330a',
      '#36B1BF': '#472a06',
    },
  },
};

// Brand-accent emboss maps: on bronze/silver seals the quill embosses in shades of
// the plan's brand colour (biggest facet ≈ the brand colour itself, outline and
// flourish darkest so the mark's structure still reads).
const ACCENT_EMBOSS: Record<SealAccent, Record<string, string>> = {
  writer: {
    currentColor: '#6e4d02',
    '#F2385A': '#c17f02',
    '#F5A503': '#ffc233',
    '#4AD9D9': '#f5a503',
    '#36B1BF': '#9c6702',
  },
  author: {
    currentColor: '#115e63',
    '#F2385A': '#178f96',
    '#F5A503': '#4ad9d9',
    '#4AD9D9': '#29b6b6',
    '#36B1BF': '#1d8a90',
  },
  bestseller: {
    currentColor: '#7e1029',
    '#F2385A': '#ff5c78',
    '#F5A503': '#f2385a',
    '#4AD9D9': '#d92a4e',
    '#36B1BF': '#a81a3c',
  },
};

/** Scalloped rosette outline: `n` outward arc bumps between valley points on `rValley`. */
function rosettePath(cx: number, cy: number, rValley: number, n: number, flat = 0.62): string {
  const pts: Array<[number, number]> = [];
  for (let k = 0; k <= n; k++) {
    const a = (2 * Math.PI * k) / n - Math.PI / 2;
    pts.push([cx + rValley * Math.cos(a), cy + rValley * Math.sin(a)]);
  }
  const chord = 2 * rValley * Math.sin(Math.PI / n);
  const rArc = (chord * flat).toFixed(2);
  let d = `M${pts[0][0].toFixed(2)},${pts[0][1].toFixed(2)}`;
  for (let k = 1; k <= n; k++) {
    d += ` A${rArc},${rArc} 0 0 1 ${pts[k][0].toFixed(2)},${pts[k][1].toFixed(2)}`;
  }
  return d + ' Z';
}

function embossedQuill(idSuffix: string, emboss: Record<string, string>): string {
  let frag = QUILL_MARK;
  for (const [src, dst] of Object.entries(emboss)) {
    frag = frag.replaceAll(`fill="${src}"`, `fill="${dst}"`);
  }
  for (let k = 0; k < 5; k++) {
    frag = frag.replaceAll(`mask${k}_13_127`, `sealM${k}-${idSuffix}`);
  }
  return frag;
}

export interface SealOptions {
  metal: SealMetal;
  /** Engraved around the top arc, e.g. "LIFETIME MEMBER" or "AUTHOR MEMBER". */
  topText: string;
  /** Brand-colour the quill emboss; omit for the plain metal emboss. */
  accent?: SealAccent | null;
  /** Scalloped award-sticker edge. Defaults on for gold (lifetime), off otherwise. */
  frill?: boolean;
  /** Uniquifies the SVG's internal ids so several seals can coexist in one page. */
  idSuffix: string;
  ariaLabel?: string;
}

function escapeXml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Builds a Dabble member-seal SVG: a metal disc with the quill mark embossed in the centre. */
export function buildSealSvg({
  metal,
  topText: rawTopText,
  accent,
  frill = metal === 'gold',
  idSuffix,
  ariaLabel,
}: SealOptions): string {
  const topText = escapeXml(rawTopText);
  const p = PALETTES[metal];
  const facets = embossedQuill(idSuffix, accent ? ACCENT_EMBOSS[accent] : p.emboss);
  // Quill mark bbox: x 1.5–72, y 1–70 → centre (36.8, 35.5), scaled to sit inside the inner ring.
  const s = 0.66;
  const tx = (60 - 36.8 * s).toFixed(2);
  const ty = (58.5 - 35.5 * s).toFixed(2);
  const [topSize, topSpacing] = rawTopText.length > 19 ? [7.8, 1] : rawTopText.length > 15 ? [8.8, 1.3] : [10, 2];
  const edge = frill
    ? `<path d="${rosettePath(60, 60, 50, 26)}" fill="url(#sealGold-${idSuffix})"/>
  <path d="${rosettePath(60, 60, 50, 26)}" fill="none" stroke="${p.rim}" stroke-width="1" opacity="0.55"/>`
    : `<circle cx="60" cy="60" r="54" fill="url(#sealGold-${idSuffix})"/>
  <circle cx="60" cy="60" r="54" fill="none" stroke="${p.rim}" stroke-width="1" opacity="0.55"/>`;
  const label = ariaLabel ? ` role="img" aria-label="${escapeXml(ariaLabel)}"` : ' aria-hidden="true"';
  return `<svg viewBox="0 0 120 120"${label} xmlns="http://www.w3.org/2000/svg">
  <defs>
    <radialGradient id="sealGold-${idSuffix}" cx="35%" cy="28%" r="85%">
      <stop offset="0%" stop-color="${p.g0}"/>
      <stop offset="55%" stop-color="${p.g1}"/>
      <stop offset="100%" stop-color="${p.g2}"/>
    </radialGradient>
    <path id="sealTop-${idSuffix}" d="M 26.19,69.06 A 35,35 0 1 1 93.81,69.06" fill="none"/>
    <path id="sealBottom-${idSuffix}" d="M 18,60 A 42,42 0 0 0 102,60" fill="none"/>
    <clipPath id="sealFace-${idSuffix}"><circle cx="60" cy="60" r="47.5"/></clipPath>
  </defs>
  ${edge}
  <circle cx="60" cy="60" r="47.5" fill="none" stroke="${p.ring}" stroke-width="1" opacity="0.5"/>
  <circle cx="60" cy="60" r="30" fill="none" stroke="${p.ring}" stroke-width="1" opacity="0.4"/>
  <text font-family="'DM Sans Variable', 'DM Sans', sans-serif" font-size="${topSize}" font-weight="750" letter-spacing="${topSpacing}" fill="${p.ink}">
    <textPath href="#sealTop-${idSuffix}" startOffset="50%" text-anchor="middle">${topText}</textPath>
  </text>
  <text font-family="'DM Sans Variable', 'DM Sans', sans-serif" font-size="8.5" font-weight="750" letter-spacing="3" fill="${p.ink}">
    <textPath href="#sealBottom-${idSuffix}" startOffset="50%" text-anchor="middle">&#10022;&#160;&#160;DABBLE&#160;&#160;&#10022;</textPath>
  </text>
  <g transform="translate(${tx},${ty}) scale(${s})">
    ${facets}
  </g>
  <ellipse cx="38" cy="28" rx="36" ry="20" fill="#ffffff" opacity="0.12" transform="rotate(-24 38 28)" clip-path="url(#sealFace-${idSuffix})"/>
</svg>`;
}

/** Maps a Stripe plan/product name to its brand accent, if it is one of the known tiers. */
export function accentForPlanName(name: string | null | undefined): SealAccent | null {
  const n = (name ?? '').toLowerCase();
  if (n.includes('bestseller')) return 'bestseller';
  if (n.includes('author')) return 'author';
  if (n.includes('writer')) return 'writer';
  return null;
}
